import React, { useEffect, useState } from "react";
import ActionButton from "../../components/ui/ActionButton";
import { Card } from "../../components/ui/Card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/Switch";
import {
  useAddShiftMutation,
  useUpdateShiftMutation,
} from "../../features/shifts/shiftApi";

const initialFormData = {
  plant_id: "",
  shift_name: "",
  start_time: "",
  end_time: "",
  shift_status: true,
};

const getShiftUid = (row) => row?.uid || row?._id || row?.id || row?.shift_id;

const toInputValue = (value) =>
  value === null || value === undefined ? "" : String(value);

const toTimeInputValue = (value) => (value ? String(value).slice(0, 5) : "");

const getRequestErrorMessage = (error) =>
  error?.data?.message ||
  error?.error ||
  error?.message ||
  "Unable to save shift.";

const ShiftForm = ({ editData = null, isOpen, onClose, selectedPlantId }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [errorMessage, setErrorMessage] = useState("");

  const [addShift, { isLoading: isAdding }] = useAddShiftMutation();
  const [updateShift, { isLoading: isUpdating }] = useUpdateShiftMutation();
  const isSaving = isAdding || isUpdating;

  useEffect(() => {
    if (!isOpen) return;

    if (!editData) {
      setFormData({ ...initialFormData, plant_id: selectedPlantId || "" });
      setErrorMessage("");
      return;
    }

    setFormData({
      plant_id: toInputValue(editData.plant_id),
      shift_name: editData.shift_name || "",
      start_time: toTimeInputValue(editData.start_time),
      end_time: toTimeInputValue(editData.end_time),
      shift_status: editData.shift_status ?? true,
    });
    setErrorMessage("");
  }, [editData, isOpen, selectedPlantId]);

  if (!isOpen) {
    return null;
  }

  const handleFieldValueChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMessage("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    handleFieldValueChange(name, value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    const payload = {
      shift_name: formData.shift_name.trim(),
      start_time: formData.start_time,
      end_time: formData.end_time,
      shift_status: formData.shift_status,
    };

    if (!editData) {
      payload.plant_id = formData.plant_id;
    }

    if (
      !payload.shift_name ||
      !payload.start_time ||
      !payload.end_time ||
      (!editData && !payload.plant_id)
    ) {
      setErrorMessage("Plant, shift name, start time, and end time are required.");
      return;
    }

    try {
      if (editData) {
        await updateShift({ uid: getShiftUid(editData), ...payload }).unwrap();
      } else {
        await addShift(payload).unwrap();
      }

      onClose();
    } catch (error) {
      setErrorMessage(getRequestErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center overflow-y-auto bg-black/40 p-3 sm:p-4">
      <Card
        as="form"
        onSubmit={handleSubmit}
        className="max-h-[calc(100dvh-1.5rem)] w-full max-w-md overflow-y-auto p-4 font-medium text-darkGray shadow-lg sm:max-h-[calc(100dvh-2rem)] sm:p-5"
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">
            {editData ? "Edit Shift" : "Add Shift"}
          </h2>
          <ActionButton action="cancel" iconOnly variant="ghost" onClick={onClose} />
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="shift_name">Shift Name</Label>
            <Input
              id="shift_name"
              name="shift_name"
              value={formData.shift_name}
              onChange={handleChange}
              placeholder="Example: Morning"
            />
          </div>

          <div>
            <Label htmlFor="start_time">Start Time</Label>
            <Input
              id="start_time"
              name="start_time"
              type="time"
              value={formData.start_time}
              onChange={handleChange}
            />
          </div>

          <div>
            <Label htmlFor="end_time">End Time</Label>
            <Input
              id="end_time"
              name="end_time"
              type="time"
              value={formData.end_time}
              onChange={handleChange}
            />
          </div>

          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
            <div>
              <Label htmlFor="shift_status" className="mb-0">
                Status
              </Label>
              <p className="text-xs text-darkGray">
                {formData.shift_status ? "Active" : "Inactive"}
              </p>
            </div>
            <Switch
              checked={formData.shift_status}
              onCheckedChange={(value) =>
                setFormData((prev) => ({ ...prev, shift_status: value }))
              }
            />
          </div>

          {errorMessage && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-600">
              {errorMessage}
            </p>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <ActionButton action="cancel" variant="outline" onClick={onClose} />
          <ActionButton
            action={editData ? "update" : "submit"}
            type="submit"
            isLoading={isSaving}
            label={editData ? "Update Shift" : "Create Shift"}
          />
        </div>
      </Card>
    </div>
  );
};

export default ShiftForm;
